import { css } from 'aphrodite';
import * as React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { styleSheet } from './styles';

interface PremiumAndAverageByCarrierChartProps {
	premiumByCarrier?: {
		title: string;
		visualizationType: string;
		data: {
			id: string;
			label: string;
			count: number;
			value: number;
		}[];
	};
	averagePremiumByCarrier?: {
		title: string;
		visualizationType: string;
		data: {
			id: string;
			label: string;
			count: number;
			value: number;
		}[];
	};
	isLoading?: boolean;
	className?: string;
}

const formatCurrencyAxisLabel = (value: number): string => {
	if (value >= 1000) {
		const thousands = value / 1000;
		// If it's a whole number, don't show decimals
		if (thousands % 1 === 0) {
			return `$${thousands}k`;
		}
		// Otherwise show one decimal place
		return `$${thousands.toFixed(1)}k`;
	}
	// For values less than 1000, show as whole number without cents
	return `$${Math.round(value)}`;
};

export const PremiumAndAverageByCarrierChart: React.FC<PremiumAndAverageByCarrierChartProps> = ({
	premiumByCarrier,
	averagePremiumByCarrier,
	isLoading,
	className,
}) => {
	const chartData = React.useMemo<
		{
			label: string;
			totalPremium: number;
			averagePremium: number;
			totalCount: number;
			averageCount: number;
		}[]
	>(() => {
		// Create a map to merge data by carrier label
		const carrierMap = new Map<
			string,
			{
				label: string;
				totalPremium: number;
				averagePremium: number;
				totalCount: number;
				averageCount: number;
			}
		>();

		// Add total premium data
		if (premiumByCarrier?.data && premiumByCarrier.data.length > 0) {
			premiumByCarrier.data.forEach(item => {
				carrierMap.set(item.label, {
					label: item.label,
					totalPremium: item.value,
					averagePremium: 0,
					totalCount: item.count,
					averageCount: 0,
				});
			});
		}

		// Add average premium data
		if (averagePremiumByCarrier?.data && averagePremiumByCarrier.data.length > 0) {
			averagePremiumByCarrier.data.forEach(item => {
				const existing = carrierMap.get(item.label);
				if (existing) {
					existing.averagePremium = item.value;
					existing.averageCount = item.count;
				} else {
					carrierMap.set(item.label, {
						label: item.label,
						totalPremium: 0,
						averagePremium: item.value,
						totalCount: 0,
						averageCount: item.count,
					});
				}
			});
		}

		// Convert map to array and sort by total premium (descending)
		return Array.from(carrierMap.values()).sort((a, b) => b.totalPremium - a.totalPremium);
	}, [premiumByCarrier, averagePremiumByCarrier]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No premium data available</div>
			</div>
		);
	}

	const CustomTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const tooltipData = payload[0].payload;
			return (
				<div className={css(styleSheet.tooltip)}>
					<p className={css(styleSheet.tooltipLabel)}>{tooltipData.label}</p>
					{tooltipData.totalPremium > 0 && (
						<p className={css(styleSheet.tooltipValue)}>Total Premium: {formatCurrency(tooltipData.totalPremium)}</p>
					)}
					{tooltipData.totalCount > 0 && (
						<p className={css(styleSheet.tooltipValue)}>Total Count: {tooltipData.totalCount}</p>
					)}
					{tooltipData.averagePremium > 0 && (
						<p className={css(styleSheet.tooltipValue)}>
							Average Premium: {formatCurrency(tooltipData.averagePremium)}
						</p>
					)}
					{tooltipData.averageCount > 0 && (
						<p className={css(styleSheet.tooltipValue)}>Average Count: {tooltipData.averageCount}</p>
					)}
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h3 className={css(styleSheet.title)}>{premiumByCarrier?.title || 'Premium and Average Premium by Carrier'}</h3>
			<ResponsiveContainer width='100%' height={400}>
				<BarChart data={chartData} margin={{ top: 10, right: 80, left: 60, bottom: 60 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='label'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						angle={-45}
						textAnchor='end'
						height={100}
					/>
					<YAxis
						yAxisId='left'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						tickFormatter={formatCurrencyAxisLabel}
						label={{
							value: 'Total Renewals',
							angle: -90,
							position: 'insideLeft',
							style: { textAnchor: 'middle', fill: THEME.colors.text.main, fontSize: 12 },
						}}
					/>
					<YAxis
						yAxisId='right'
						orientation='right'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 12, fill: THEME.colors.text.main }}
						tickFormatter={formatCurrencyAxisLabel}
						label={{
							value: 'Average Renewals',
							angle: 90,
							position: 'insideRight',
							style: { textAnchor: 'middle', fill: THEME.colors.text.main, fontSize: 12 },
						}}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Bar
						yAxisId='left'
						dataKey='totalPremium'
						fill={THEME.colors.chart.levBlue}
						radius={[4, 4, 0, 0]}
						name='Total Renewals'
					/>
					<Bar
						yAxisId='right'
						dataKey='averagePremium'
						fill={THEME.colors.chart.enchantment}
						radius={[4, 4, 0, 0]}
						name='Average Premium'
					/>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
